import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import type { PackageCategory, ProductStatus } from "@nh/shared";
import { Button, Select } from "@/shared/ui/core";
import { FilterBar, useStatusOptions } from "@/features/admin/components";
import { usePackageCategoryOptions } from "./PackageCategoryLabel";
import { PACKAGE_SORTS, type PackageFilters, type PackageSort } from "../filters";

export interface PackagesToolbarProps {
  filters: PackageFilters;
  onChange: (patch: Partial<PackageFilters>) => void;
  onReset: () => void;
  /** `true` cuando hay algo que limpiar. */
  canReset: boolean;
}

const sortLabels: Record<PackageSort, string> = {
  catalog: "Orden del catálogo",
  "discount-desc": "Mayor descuento",
  "price-asc": "Precio: de menor a mayor",
  "price-desc": "Precio: de mayor a menor",
  newest: "Más recientes",
};

/**
 * Filtrar y ordenar la lista de paquetes.
 *
 * No hay buscador, y no es un olvido: `packageListQuerySchema` no
 * declara `search`, así que un campo de búsqueda solo podría filtrar la
 * página que ya está en pantalla. Un buscador que no busca en todo es
 * peor que no tenerlo — quien escribe "digital" y no ve nada concluye
 * que no existe. Los paquetes son pocos por diseño (dos categorías,
 * unos cuantos en cada una), así que los dos filtros bastan.
 *
 * Los filtros de categoría y estado SÍ viajan al API. El orden no: ver
 * la nota de `filters.ts`.
 */
export function PackagesToolbar({
  filters,
  onChange,
  onReset,
  canReset,
}: PackagesToolbarProps) {
  const { t } = useTranslation();
  const statusOptions = useStatusOptions();
  const categoryOptions = usePackageCategoryOptions();

  return (
    <FilterBar
      actions={
        canReset ? (
          <Button variant="ghost" size="sm" leftIcon={<X />} onClick={onReset}>
            {t("Limpiar filtros")}
          </Button>
        ) : undefined
      }
    >
      <Select
        label={t("Categoría")}
        placeholder={t("Todas las categorías")}
        value={filters.category}
        options={categoryOptions}
        onChange={(event) =>
          onChange({ category: event.target.value as PackageCategory | "" })
        }
        fieldClassName="min-w-[210px]"
      />

      <Select
        label={t("Estado")}
        placeholder={t("Todos los estados")}
        value={filters.status}
        options={statusOptions}
        onChange={(event) =>
          onChange({ status: event.target.value as ProductStatus | "" })
        }
        fieldClassName="min-w-[170px]"
      />

      <Select
        label={t("Ordenar por")}
        value={filters.sort}
        options={PACKAGE_SORTS.map((sort) => ({
          value: sort,
          label: t(sortLabels[sort]),
        }))}
        onChange={(event) => onChange({ sort: event.target.value as PackageSort })}
        fieldClassName="min-w-[210px]"
      />
    </FilterBar>
  );
}
